import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { 
  MagnifyingGlassIcon, 
  CheckCircleIcon, 
  XCircleIcon,
  ExclamationTriangleIcon,
  ArrowRightIcon,
  BuildingOfficeIcon,
  CurrencyDollarIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';
import axios from '../config/api';
import CompanyLogo from '../components/common/CompanyLogo';
import TickerSearchInput from '../components/common/TickerSearchInput';
import AuthenticatedNavigation from '../components/common/AuthenticatedNavigation';
import { useAuth } from '../hooks/useAuth';

interface EligibilityResult {
  eligible: boolean;
  maxFacilitySize?: number;
  companyName?: string;
  ticker?: string;
  marketCap?: number;
  reason?: string;
  facilityId?: string;
}

const EligibilityCheck: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [ticker, setTicker] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [result, setResult] = useState<EligibilityResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Check for ticker in URL parameters on component mount
  useEffect(() => {
    const urlTicker = searchParams.get('ticker');
    if (urlTicker) {
      setTicker(urlTicker);
      // Auto-trigger search if ticker is provided in URL
      handleSearchWithTicker(urlTicker);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleSearchWithTicker = async (tickerSymbol: string) => {
    setIsSearching(true);
    setError(null);
    setResult(null);

    try {
      const response = await axios.post('/api/eligibility', {
        ticker: tickerSymbol.toUpperCase()
      });

      // Store the results in app state by navigating
      const eligibilityData = response.data;
      setResult(eligibilityData);
      
      if (eligibilityData.eligible) {
        // Navigate to success page after a short delay to show the result
        setTimeout(() => {
          navigate('/eligibility-success', { 
            state: { 
              results: eligibilityData 
            } 
          });
        }, 1500);
      }
    } catch (err: any) {
      console.error('Eligibility check error:', err);
      console.error('Error response:', err.response);
      console.error('Error status:', err.response?.status);
      console.error('Error data:', err.response?.data);
      
      // More detailed error message
      const errorMessage = err.response?.data?.error || 
                          err.response?.data?.message || 
                          err.message || 
                          'An error occurred while checking eligibility';
      setError(errorMessage);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (ticker.trim()) {
      handleSearchWithTicker(ticker);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header - Matching Landing Page */}
      <header className="bg-white border-b border-gray-200">
        {/* Top Utility Bar */}
        <div className="bg-gray-50 border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-end items-center h-10 text-sm">
              {/* <a href="#demo" className="text-gray-700 hover:text-navy-700 ml-6">Schedule a Demo</a> */}
              <a href="#support" className="text-gray-700 hover:text-navy-700 ml-6">Customer Service</a>
              {isAuthenticated ? (
                <button 
                  onClick={() => navigate('/dashboard')}
                  className="ml-6 text-navy-700 font-medium hover:text-navy-900"
                >
                  Dashboard
                </button>
              ) : (
                <button 
                  onClick={() => navigate('/login')}
                  className="ml-6 text-navy-700 font-medium hover:text-navy-900"
                >
                  Login
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Main Navigation */}
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-24">
            {/* Logo */}
            <AuthenticatedNavigation className="flex items-center cursor-pointer">
              <img src="/24lp-logo-primary.png" alt="Twenty Four Capital" className="h-20 w-auto" />
            </AuthenticatedNavigation>
            
            {/* Navigation Links */}
            <div className="hidden md:flex items-center space-x-8">
              <a href="/#how-it-works" className="text-gray-700 hover:text-navy-700 font-medium">How it Works</a>
              <a href="/#platform-features" className="text-gray-700 hover:text-navy-700 font-medium">Platform Features</a>
              <a href="/#why-us" className="text-gray-700 hover:text-navy-700 font-medium">Why Choose Us</a>
            </div>

            {/* CTA Button */}
            {isAuthenticated ? (
              <button 
                onClick={() => navigate('/dashboard')}
                className="bg-orange-500 text-white px-6 py-2.5 rounded-md font-medium hover:bg-orange-600 transition-colors"
              >
                Dashboard
              </button>
            ) : (
              <button 
                onClick={() => navigate('/login')}
                className="bg-orange-500 text-white px-6 py-2.5 rounded-md font-medium hover:bg-orange-600 transition-colors"
              >
                Login
              </button>
            )}
          </div>
        </nav>
      </header>

      {/* Main Content */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Check Your Eligibility
            </h1>
            <p className="text-xl text-gray-600">
              Enter your ticker symbol to instantly check if your company qualifies for equity financing
            </p>
          </motion.div>

          {/* Search Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="bg-white rounded-xl shadow-lg p-8 mb-8"
          >
            <form onSubmit={handleSearch} className="space-y-6">
              <div>
                <label htmlFor="ticker" className="block text-sm font-medium text-gray-700 mb-2">
                  Stock Ticker Symbol
                </label>
                <TickerSearchInput
                  value={ticker}
                  onChange={(value) => setTicker(value)}
                  onSelect={(option) => {
                    setTicker(option.symbol);
                    // Optionally store the company name for later use
                    if (option.name) {
                      sessionStorage.setItem(`ticker_${option.symbol}_name`, option.name);
                    }
                  }}
                  placeholder="Search by company name or ticker symbol"
                  disabled={isSearching}
                  required
                  showPopularOnFocus={true}
                />
              </div>

              <button
                type="submit"
                disabled={isSearching || !ticker.trim()}
                className="w-full bg-orange-500 text-white py-3 px-6 rounded-lg font-semibold text-lg hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {isSearching ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Checking Eligibility...
                  </>
                ) : (
                  'Check Eligibility'
                )}
              </button>
            </form>
          </motion.div>

          {/* Error Message */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-red-50 border border-red-200 rounded-lg p-4 mb-8"
            >
              <div className="flex items-center">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-600 mr-2" />
                <p className="text-red-800">{error}</p>
              </div>
            </motion.div>
          )}

          {/* Results */}
          {result && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className={`rounded-xl shadow-lg p-8 ${
                result.eligible ? 'bg-green-50 border-2 border-green-200' : 'bg-red-50 border-2 border-red-200'
              }`}
            >
              <div className="text-center mb-6">
                {result.eligible ? (
                  <>
                    <div className="flex justify-center items-center mb-4">
                      <CheckCircleIcon className="h-16 w-16 text-green-600" />
                    </div>
                    <div className="flex justify-center items-center gap-4 mb-4">
                      {result.ticker && (
                        <CompanyLogo 
                          ticker={result.ticker} 
                          companyName={result.companyName} 
                          size="lg" 
                        />
                      )}
                      <div>
                        <h2 className="text-2xl font-bold text-green-900">
                          Congratulations! You're Eligible
                        </h2>
                        <p className="text-green-700">
                          {result.companyName} qualifies for equity financing
                        </p>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <XCircleIcon className="h-16 w-16 text-red-600 mx-auto mb-4" />
                    <h2 className="text-2xl font-bold text-red-900 mb-2">
                      Not Currently Eligible
                    </h2>
                    <p className="text-red-700">{result.reason}</p>
                  </>
                )}
              </div>

              {result.eligible && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    <div className="bg-white rounded-lg p-4 text-center">
                      <BuildingOfficeIcon className="h-8 w-8 text-gray-600 mx-auto mb-2" />
                      <p className="text-sm text-gray-600">Company</p>
                      <p className="font-semibold text-gray-900">{result.companyName}</p>
                      <p className="text-sm text-gray-500">{result.ticker}</p>
                    </div>
                    <div className="bg-white rounded-lg p-4 text-center">
                      <ChartBarIcon className="h-8 w-8 text-gray-600 mx-auto mb-2" />
                      <p className="text-sm text-gray-600">Market Cap</p>
                      <p className="font-semibold text-gray-900">
                        {result.marketCap ? formatCurrency(result.marketCap) : 'N/A'}
                      </p>
                    </div>
                    <div className="bg-white rounded-lg p-4 text-center">
                      <CurrencyDollarIcon className="h-8 w-8 text-gray-600 mx-auto mb-2" />
                      <p className="text-sm text-gray-600">Max Facility Size</p>
                      <p className="font-semibold text-gray-900">
                        {result.maxFacilitySize ? formatCurrency(result.maxFacilitySize) : 'N/A'}
                      </p>
                    </div>
                  </div>

                  <div className="text-center">
                    <p className="text-gray-600 mb-4">Redirecting to deal options...</p>
                    <div className="flex justify-center">
                      <ArrowRightIcon className="h-5 w-5 text-green-600 animate-pulse" />
                    </div>
                  </div>
                </>
              )}
            </motion.div>
          )}

          {/* Info Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="mt-12 text-center"
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Why Check Eligibility?
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl mx-auto">
              <div className="text-center">
                <div className="bg-orange-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
                  <MagnifyingGlassIcon className="h-6 w-6 text-orange-600" />
                </div>
                <h4 className="font-medium text-gray-900 mb-1">Instant Results</h4>
                <p className="text-sm text-gray-600">
                  Get immediate feedback on your eligibility status
                </p>
              </div>
              <div className="text-center">
                <div className="bg-orange-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
                  <ChartBarIcon className="h-6 w-6 text-orange-600" />
                </div>
                <h4 className="font-medium text-gray-900 mb-1">Real Market Data</h4>
                <p className="text-sm text-gray-600">
                  Based on current trading data and market conditions
                </p>
              </div>
              <div className="text-center">
                <div className="bg-orange-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
                  <CurrencyDollarIcon className="h-6 w-6 text-orange-600" />
                </div>
                <h4 className="font-medium text-gray-900 mb-1">Customized Terms</h4>
                <p className="text-sm text-gray-600">
                  Receive financing options tailored to your company
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default EligibilityCheck; 